/*
 * Silarius — typo-tolerant storefront search.
 *
 * Shopify's native product search has no spelling correction. A mistyped term
 * either returns nothing at all ("AL600ULACMCV" -> 0 results) or quietly drops
 * out of a multi-word query so the results rank on whatever else matched
 * ("hikvison camera" -> a page of cameras, none of them Hikvision). Either way
 * the shopper has to already know the exact spelling, which is the complaint
 * this file exists to fix.
 *
 * Two different repairs, because the two kinds of token fail differently:
 *
 *   Words ("hikvison", "camra", "acess")
 *     Corrected against assets/silarius-search-dict.json, which holds every word
 *     that occurs anywhere Shopify indexes — titles, vendors, types, tags,
 *     descriptions, SKUs. Membership is the test: a token that is not in that
 *     list cannot match anything properly, so it is a typo. Rare-but-real brands
 *     ("abus", "acti", "ubiquiti" each appear in a single description here) are
 *     in the list precisely so they are never "corrected" into something else.
 *
 *   Model numbers ("AL600ULACMCV", "KPR2001")
 *     Not in the dictionary at all — they are precise identifiers, so the
 *     storefront itself is a better oracle than any shipped list. The client
 *     shortens the token and re-asks until the catalogue answers:
 *     AL600ULACMCV -> AL600ULACMC* -> the AL600ULACMCB it was meant to be.
 *
 * Nothing is rewritten on a guess: a repair is only used once it has been shown
 * to return results, and the shopper always gets a one-click way back to what
 * they actually typed.
 */
(function () {
  'use strict';

  var CFG = window.SilariusSearch || {};
  var DICT_URL = CFG.dictUrl;
  var SEARCH_URL = CFG.searchUrl || '/search';
  if (!DICT_URL) return;

  var MIN_TOKEN = 3;         // shorter than this is too ambiguous to correct
  var MODEL_MIN_STEM = 4;    // never shorten a model past this
  var MAX_PROBES = 12;       // hard ceiling on extra requests per search
  var MAX_SPLIT = 7;         // how far in to look for a missing separator
  var PARAM_ORIGINAL = 'sc'; // carries the original spelling across the redirect
  var INSISTED = '!';        // sentinel: shopper asked for their spelling verbatim

  var probes = 0;

  /* ---------------------------------------------------------------- dictionary */

  var dict = null;
  var dictPromise = null;

  function loadDict() {
    if (dictPromise) return dictPromise;
    dictPromise = fetch(DICT_URL, { credentials: 'omit' })
      .then(function (r) { return r.json(); })
      .then(function (json) {
        var list = json.w ? json.w.split(' ') : [];
        var rank = Object.create(null);
        var byLen = Object.create(null);
        for (var i = 0; i < list.length; i++) {
          var t = list[i];
          if (rank[t] === undefined) rank[t] = i;
          (byLen[t.length] || (byLen[t.length] = [])).push(t);
        }
        // Identifiers that contain a separator, keyed by that separator stripped
        // out, so "silb48m3poe1g500" resolves to "sil-b48m3poe1g500".
        var models = Object.create(null);
        // Second key on the same data, with the characters people mix up in a
        // part number folded together (O/0, I/L/1, S/5, B/8, Z/2). Catches
        // "SILB48M3POE1G5OO" for ...G500. Keys that two different identifiers
        // both fold onto are dropped rather than guessed between.
        var folded = Object.create(null);
        if (json.m) {
          var ms = json.m.split(' ');
          for (var k = 0; k < ms.length; k++) {
            var bare = ms[k].replace(/[^a-z0-9]+/g, '');
            // A real separator-bearing identifier is at least 5 characters once
            // stripped (the build's own floor). Anything shorter is a fragment and
            // would rewrite ordinary numbers ("12" -> "1/2").
            if (bare.length < 5) continue;
            models[bare] = ms[k];
            var f = confusableFold(bare);
            if (folded[f] === undefined) folded[f] = ms[k];
            else if (folded[f] !== ms[k]) folded[f] = null;   // ambiguous
          }
        }
        dict = {
          rank: rank,
          byLen: byLen,
          sorted: list.slice().sort(),
          // Everything is recognised; only the common head is offered as a fix.
          suggestable: json.wc || list.length,
          models: models,
          folded: folded,
          // Words of live brand names ("grandstream", "doorking").
          brands: (function () {
            var b = Object.create(null);
            if (json.b) json.b.split(' ').forEach(function (w) { b[w] = true; });
            return b;
          })(),
          // Suggested searches, most common first.
          phrases: json.p ? json.p.split('|') : []
        };
        return dict;
      })
      .catch(function () { dict = null; return null; });
    return dictPromise;
  }

  /* ------------------------------------------------------------------ matching */

  function tokenize(q) {
    return (q || '').toLowerCase().match(/[a-z0-9]+/g) || [];
  }

  function isModel(t) { return /\d/.test(t); }
  function isKnownWord(t) {
    return dict.rank[t] !== undefined || realWords[t] === true || STOP[t] === true;
  }

  // The dictionary build skips these as noise, so they are never in the list -
  // which used to make them look like typos: "pach and company" became
  // "pach android company" and "the ..." became "these ...". Keep in step with
  // STOP in tools/build-search-dict.py.
  var STOP = Object.create(null);
  ('the a an and or of for with to in on at by from is are be as it its this that')
    .split(' ').forEach(function (w) { STOP[w] = true; });

  /*
   * Words the live catalogue has been seen to contain although the shipped
   * dictionary does not. The dictionary is a snapshot; brands keep arriving
   * after it was built, and a brand missing from it used to be "corrected" into
   * its nearest neighbour ("samson" -> "samsung", 2026-10-18). Before any word
   * is treated as a typo it is asked of the storefront on its own, and if a
   * returned product actually carries it in its title or handle, it is a real
   * word and is left exactly as typed.
   */
  var realWords = Object.create(null);
  var checkedWords = Object.create(null);
  var MAX_VERIFY = 3;

  function productWords(html) {
    var seen = Object.create(null);
    if (!html || html.indexOf('ProductItem') === -1) return seen;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var nodes = doc.querySelectorAll('.ProductItem__Title, .ProductItem__Vendor');
    var text = '';
    for (var i = 0; i < nodes.length; i++) text += ' ' + nodes[i].textContent;
    var links = doc.querySelectorAll('a[href*="/products/"]');
    for (var j = 0; j < links.length; j++) {
      var h = links[j].getAttribute('href') || '';
      text += ' ' + h.split('/products/')[1].split('?')[0].replace(/-/g, ' ');
    }
    var toks = tokenize(text);
    for (var k = 0; k < toks.length; k++) seen[toks[k]] = true;
    return seen;
  }

  // Resolves once every unfamiliar word in the query has been checked against
  // the live catalogue (at most MAX_VERIFY of them, each asked once per page).
  function verifyUnknown(query) {
    var tokens = tokenize(query);
    var ask = [];
    for (var i = 0; i < tokens.length && ask.length < MAX_VERIFY; i++) {
      var t = tokens[i];
      if (isModel(t) || t.length < MIN_TOKEN || isKnownWord(t) || checkedWords[t]) continue;
      // A half-typed final word is not a typo yet; Shopify prefix-matches it.
      if (i === tokens.length - 1 && bestCompletion(t)) continue;
      checkedWords[t] = true;
      ask.push(t);
    }
    if (!ask.length) return Promise.resolve();
    return Promise.all(ask.map(function (t) {
      return fetch(sectionUrl(t), { credentials: 'same-origin' })
        .then(function (r) { return r.text(); })
        .then(function (html) { if (productWords(html)[t]) realWords[t] = true; })
        .catch(function () {});
    }));
  }

  // Characters that get swapped for one another when reading a part number off
  // a label. Folded to a single form so both spellings share a key.
  function confusableFold(s) {
    return s.replace(/o/g, '0').replace(/[il]/g, '1')
            .replace(/s/g, '5').replace(/b/g, '8').replace(/z/g, '2');
  }

  // The catalogue identifier this token was meant to be, or null.
  function canonicalModel(t) {
    var exact = dict.models[t];
    if (exact) return exact;
    var loose = dict.folded[confusableFold(t)];
    return loose || null;                       // null also covers the ambiguous marker
  }

  function lowerBound(sorted, t) {
    var lo = 0, hi = sorted.length;
    while (lo < hi) {
      var mid = (lo + hi) >> 1;
      if (sorted[mid] < t) lo = mid + 1; else hi = mid;
    }
    return lo;
  }

  // Most common catalogue word starting with t (t itself excluded).
  function bestCompletion(t) {
    var best = null, bestRank = Infinity;
    for (var i = lowerBound(dict.sorted, t); i < dict.sorted.length; i++) {
      var cand = dict.sorted[i];
      if (cand.lastIndexOf(t, 0) !== 0) break;
      if (cand === t) continue;
      var r = dict.rank[cand];
      if (r < bestRank && r < dict.suggestable) { bestRank = r; best = cand; }
    }
    return best;
  }

  // Optimal string alignment distance, abandoned once it exceeds max.
  function distance(a, b, max) {
    var la = a.length, lb = b.length;
    if (Math.abs(la - lb) > max) return max + 1;
    if (a === b) return 0;

    var prev2 = new Array(lb + 1);
    var prev = new Array(lb + 1);
    var cur = new Array(lb + 1);
    for (var j = 0; j <= lb; j++) prev[j] = j;

    for (var i = 1; i <= la; i++) {
      cur[0] = i;
      var rowBest = i;
      for (var k = 1; k <= lb; k++) {
        var cost = a.charCodeAt(i - 1) === b.charCodeAt(k - 1) ? 0 : 1;
        var v = prev[k] + 1;
        if (cur[k - 1] + 1 < v) v = cur[k - 1] + 1;
        if (prev[k - 1] + cost < v) v = prev[k - 1] + cost;
        if (i > 1 && k > 1 &&
            a.charCodeAt(i - 1) === b.charCodeAt(k - 2) &&
            a.charCodeAt(i - 2) === b.charCodeAt(k - 1) &&
            prev2[k - 2] + 1 < v) {
          v = prev2[k - 2] + 1;                     // transposition
        }
        cur[k] = v;
        if (v < rowBest) rowBest = v;
      }
      if (rowBest > max) return max + 1;
      var spare = prev2; prev2 = prev; prev = cur; cur = spare;
    }
    return prev[lb];
  }

  // How many leading characters two words share. A typo usually leaves the
  // start of the word intact, so this separates candidates that tie on edit
  // distance far better than frequency does.
  function sharedPrefix(a, b) {
    var n = a.length < b.length ? a.length : b.length, i = 0;
    while (i < n && a.charCodeAt(i) === b.charCodeAt(i)) i++;
    return i;
  }

  function nearest(token, max, requireEdgeMatch) {
    var best = null, bestDist = max + 1, bestRank = Infinity, bestPrefix = -1;
    var first = token.charCodeAt(0);
    var last = token.charCodeAt(token.length - 1);

    for (var L = token.length - max; L <= token.length + max; L++) {
      var bucket = dict.byLen[L];
      if (!bucket) continue;
      for (var i = 0; i < bucket.length; i++) {
        var cand = bucket[i];
        var rank = dict.rank[cand];
        if (rank >= dict.suggestable) continue;      // too rare to suggest
        // Typos rarely damage both ends of a word. This prefilter is what keeps
        // the scan cheap; it is dropped on a second pass if nothing matched.
        if (requireEdgeMatch &&
            cand.charCodeAt(0) !== first &&
            cand.charCodeAt(cand.length - 1) !== last) continue;
        var d = distance(token, cand, max);
        if (d > max) continue;
        // Distance first, then shared prefix, and only then frequency. Rank
        // alone used to decide ties, which broke as soon as a fragment of a
        // hyphenated brand got common: "altronics" tied at distance 2 between
        // "altronix" and "tronics" (from Det-Tronics), and frequency picked
        // "tronics". Prefix length picks the one the shopper started typing.
        var pfx = sharedPrefix(token, cand);
        if (d < bestDist ||
            (d === bestDist && (pfx > bestPrefix ||
                               (pfx === bestPrefix && rank < bestRank)))) {
          best = cand; bestDist = d; bestRank = rank; bestPrefix = pfx;
        }
      }
    }
    return best;
  }

  // "liftmasterremote" -> "liftmaster remote"
  function splitWord(t) {
    if (t.length < 6) return null;
    for (var i = MIN_TOKEN; i <= t.length - MIN_TOKEN; i++) {
      var a = t.slice(0, i), b = t.slice(i);
      if (dict.rank[a] !== undefined && dict.rank[b] !== undefined &&
          dict.rank[a] < dict.suggestable && dict.rank[b] < dict.suggestable) {
        return a + ' ' + b;
      }
    }
    return null;
  }

  function correctWord(token, isLast) {
    // A partial word is not a typo. Shopify prefix-matches the final term by
    // itself, so only earlier terms need completing.
    var completion = bestCompletion(token);
    if (completion) return isLast ? null : completion;

    var max = token.length <= 5 ? 1 : 2;
    var hit = nearest(token, max, true) || nearest(token, max, false);
    if (hit) return hit;

    // Long words absorb more damage ("survalance" is 3 edits from
    // "surveillance"). Anchoring on a shared opening keeps the wider net clean.
    if (token.length >= 8) {
      var loose = nearest(token, 3, false);
      if (loose && loose.lastIndexOf(token.slice(0, 3), 0) === 0) return loose;
    }
    return null;
  }

  /*
   * Dictionary-only pass.
   * `typo` means the rewrite is worth applying even when the shopper already has
   * results on screen — either they typed a word the catalogue does not contain,
   * or their spacing did not match it ("grand stream" for "grandstream").
   */
  function repairWords(tokens) {
    var out = [];
    var changed = false;
    var typo = false;
    // The dictionary is optional: if it failed to load, model repair still works.
    if (!dict) return { query: tokens.join(' '), changed: false, typo: false };

    for (var i = 0; i < tokens.length; i++) {
      var t = tokens[i];

      if (isModel(t)) {
        // Shopify indexes "SIL-B48M3POE1G500" as two tokens, so typing it
        // without the hyphen matches nothing whatsoever. Put the separators back.
        var canonical = canonicalModel(t);
        if (canonical && canonical !== t) {
          out.push(canonical); changed = true; typo = true; continue;
        }
        out.push(t); continue;
      }

      // Two real words that together spell a brand: "grand stream" ->
      // "grandstream", "door king" -> "doorking", "sonic wall" -> "sonicwall".
      // The build only lists brands the catalogue writes closed up far more often
      // than as two words, so "life safety" is never glued into "lifesafety".
      if (i + 1 < tokens.length && !isModel(t) && !isModel(tokens[i + 1]) &&
          t.length >= MIN_TOKEN && tokens[i + 1].length >= MIN_TOKEN &&
          dict.brands[t + tokens[i + 1]]) {
        out.push(t + tokens[i + 1]); i++; changed = true; typo = true; continue;
      }

      if (t.length < MIN_TOKEN || isKnownWord(t)) { out.push(t); continue; }

      if (!bestCompletion(t)) typo = true;

      // An exact join beats any guess: "grand stream" -> "grandstream".
      if (i + 1 < tokens.length && !isModel(tokens[i + 1])) {
        var joined = t + tokens[i + 1];
        if (isKnownWord(joined)) {
          out.push(joined); i++; changed = true; typo = true; continue;
        }
      }

      // Then the nearest real word. This has to come before splitting, or
      // "intercomm" becomes "inter comm" instead of "intercom".
      var fixed = correctWord(t, i === tokens.length - 1);
      if (fixed && fixed !== t) { out.push(fixed); changed = true; continue; }

      // A final partial word is left for Shopify's own prefix match. Splitting it
      // would break a brand mid-typing: "kantec" -> "kan tec" instead of Kantech.
      if (bestCompletion(t)) { out.push(t); continue; }

      var split = splitWord(t);
      if (split) { out.push(split); changed = true; typo = true; continue; }

      out.push(t);
    }
    return { query: out.join(' '), changed: changed, typo: typo };
  }

  /* -------------------------------------------------------------- storefront IO */

  function sectionUrl(q) {
    return SEARCH_URL + '?section_id=predictive-search&type=product' +
           '&options[prefix]=last&q=' + encodeURIComponent(q);
  }

  function resultsUrl(q) {
    return SEARCH_URL + '?type=product&options[prefix]=last&q=' + encodeURIComponent(q);
  }

  var sectionCache = Object.create(null);

  function fetchSection(q) {
    if (sectionCache[q]) return sectionCache[q];
    if (probes >= MAX_PROBES) return Promise.resolve('');
    probes++;
    sectionCache[q] = fetch(sectionUrl(q), { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .catch(function () { return ''; });
    return sectionCache[q];
  }

  function hasResults(html) { return html.indexOf('ProductItem') !== -1; }

  /*
   * Model numbers: shorten the token until the catalogue recognises it.
   * AL600ULACMCV -> AL600ULACMC* -> the AL600ULACMCB it was meant to be.
   *
   * Each stem is validated ON ITS OWN, never inside the rest of the query. Asked
   * as "ds 2cd2143g* i", a stem that matches nothing still returns a page of
   * results — the other words carry it — and the shopper gets NAS enclosures for
   * a camera part number. Asked alone, a useless stem returns nothing and is
   * correctly rejected. The probes run together, and the longest stem that
   * survives wins, so a repair costs one round trip rather than five.
   */
  function repairModels(tokens) {
    var order = [];
    for (var i = 0; i < tokens.length; i++) {
      if (isModel(tokens[i]) && tokens[i].length > MODEL_MIN_STEM) order.push(i);
    }
    order.sort(function (a, b) { return tokens[b].length - tokens[a].length; });
    if (!order.length) return Promise.resolve(null);

    var token = tokens[order[0]];

    /*
     * First: a missing separator. The index above covers everything that existed
     * when the dictionary was built, so this catches products added since.
     * Each candidate is judged on its right-hand part ALONE — "sil" would carry
     * results on its own and make any split look successful.
     */
    var splits = [];
    for (var s = 2; s <= Math.min(MAX_SPLIT, token.length - MODEL_MIN_STEM); s++) {
      splits.push(token.slice(s));
    }

    var splitStep = splits.length
      ? Promise.all(splits.map(function (right) {
          return fetchSection(right).then(function (html) {
            return hasResults(html) ? right : null;
          });
        })).then(function (found) {
          for (var n = 0; n < found.length; n++) {  // longest right-hand part first
            if (found[n]) return found[n];
          }
          return null;
        })
      : Promise.resolve(null);

    var stems = [];
    // Only the tail of a part number may be wrong. Cutting deeper starts
    // matching by coincidence: "2cd214*" returns an AKG C214 microphone for a
    // Hikvision DS-2CD2143G0-I, which is worse than admitting no match.
    var floor = Math.max(MODEL_MIN_STEM, Math.ceil(token.length * 0.75));
    for (var len = token.length - 1; len >= floor && stems.length < 4; len--) {
      stems.push(token.slice(0, len));
    }
    return splitStep.then(function (fromSplit) {
      if (fromSplit) return fromSplit;
      if (!stems.length) return null;

      return Promise.all(stems.map(function (stem) {
        return fetchSection(stem + '*').then(function (html) {
          return hasResults(html) ? stem : null;
        });
      })).then(function (found) {
        for (var n = 0; n < found.length; n++) {   // longest surviving stem wins
          if (found[n]) return found[n] + '*';
        }
        return null;
      });
    });
  }

  /* Full repair: words first (free), model probing only if still empty. */
  function repair(query, pageHasResults) {
    var tokens = tokenize(query);
    if (!tokens.length) return Promise.resolve(null);
    probes = 0;   // one budget per repair, not per page

    var words = repairWords(tokens);

    var wordStep = Promise.resolve(null);
    if (words.changed) {
      wordStep = fetchSection(words.query).then(function (html) {
        return hasResults(html) ? words.query : null;
      });
    }

    return wordStep.then(function (fixed) {
      if (fixed) return fixed;
      // Only go probing for a model when the page genuinely came up empty —
      // a shortened part number is a worse answer than real results.
      if (pageHasResults) return null;
      return repairModels(tokens);
    }).then(function (fixed) {
      if (!fixed || fixed === query.toLowerCase().trim()) return null;
      return { query: fixed, typo: words.typo };
    });
  }

  /* ------------------------------------------------------- suggested searches */

  var MAX_SUGGESTIONS = 6;

  /*
   * Refinements to offer above the products, the way a shopper expects a big
   * catalogue to behave: type "liftmaster" and get "liftmaster smart remotes",
   * "liftmaster access control". Phrases are vendor+category pairs mined from
   * the catalogue, so every one of them returns products.
   *
   * `corrected` is used when the shopper has mistyped, so "liftmastr" still
   * offers LiftMaster refinements rather than nothing.
   */
  function suggestionsFor(typed, corrected) {
    if (!dict || !dict.phrases.length) return [];
    var needles = [typed];
    if (corrected && corrected !== typed) needles.push(corrected);

    var out = [];
    var seen = Object.create(null);

    function collect(test) {
      for (var i = 0; i < dict.phrases.length && out.length < MAX_SUGGESTIONS; i++) {
        var p = dict.phrases[i];
        if (seen[p] || !test(p)) continue;
        seen[p] = true;
        out.push(p);
      }
    }

    for (var n = 0; n < needles.length; n++) {
      (function (needle) {
        if (needle.length < 2) return;
        collect(function (p) { return p !== needle && p.lastIndexOf(needle, 0) === 0; });
      })(needles[n]);
    }
    // Still thin? Allow a match on a later word ("control" -> "access control").
    for (var m = 0; m < needles.length && out.length < MAX_SUGGESTIONS; m++) {
      (function (needle) {
        if (needle.length < 3) return;
        collect(function (p) { return p.indexOf(' ' + needle) !== -1; });
      })(needles[m]);
    }
    return out;
  }

  function suggestionsMarkup(list) {
    var items = '';
    for (var i = 0; i < list.length; i++) {
      items += '<li class="SearchSuggest__Item">' +
                 '<a class="SearchSuggest__Link" href="' + escapeHtml(resultsUrl(list[i])) + '">' +
                   escapeHtml(list[i]) +
                 '</a></li>';
    }
    return '<ul class="SearchSuggest__List">' + items + '</ul>';
  }

  /* ------------------------------------------------------------------- notice */

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function noticeMarkup(shownFor, typed) {
    // The trailing wildcard is how a shortened part number is asked for; it is
    // not something to show a shopper ("al600ulacmc*").
    var label = shownFor.replace(/\*/g, '');
    return '<p class="SearchCorrection">' +
             'Showing results for <strong class="SearchCorrection__Term">' +
               escapeHtml(label) + '</strong>' +
             '<a class="SearchCorrection__Link" rel="nofollow" href="' +
               escapeHtml(resultsUrl(typed) + '&' + PARAM_ORIGINAL + '=' + encodeURIComponent(INSISTED)) +
             '">Search instead for &ldquo;' + escapeHtml(typed) + '&rdquo;</a>' +
           '</p>';
  }

  /* ------------------------------------------------------------- results page */

  function param(name) {
    return new URLSearchParams(window.location.search).get(name);
  }

  function showNotice(shownFor, typed) {
    var anchor = document.querySelector('.SectionHeader__Description') ||
                 document.querySelector('.EmptyState__Title');
    if (!anchor) return;
    var holder = document.createElement('div');
    holder.innerHTML = noticeMarkup(shownFor, typed);
    anchor.parentNode.insertBefore(holder.firstChild, anchor.nextSibling);
  }

  function initResultsPage() {
    var q = (param('q') || '').trim();
    if (!q) return;

    var original = param(PARAM_ORIGINAL);
    if (original !== null) {
      if (original !== INSISTED) showNotice(q, original);
      return;   // already been through here; never loop
    }

    var pageHasResults = !document.querySelector('.EmptyState');

    loadDict().then(function (d) {
      if (!d) return null;
      return verifyUnknown(q).then(function () { return d; });
    }).then(function (d) {
      if (!d) return;
      var quick = repairWords(tokenize(q));
      // With results already on screen, only step in when the shopper typed a
      // word the catalogue does not contain at all.
      if (pageHasResults && !quick.typo) return;

      repair(q, pageHasResults).then(function (fix) {
        if (!fix) return;
        window.location.replace(
          resultsUrl(fix.query) + '&' + PARAM_ORIGINAL + '=' + encodeURIComponent(q)
        );
      });
    });
  }

  /* -------------------------------------------------------- predictive dropdown */

  /*
   * The theme rewrites .Search__Results wholesale on every keystroke, so the
   * suggestions have to be re-attached each time. `data-for` records which query
   * the current block belongs to: without it, our own insertion would retrigger
   * the observer forever.
   */
  function renderSuggestions(results, typed) {
    var existing = results.querySelector('.SearchSuggest');
    if (existing && existing.getAttribute('data-for') === typed) return;

    var corrected = null;
    var fix = repairWords(tokenize(typed));
    if (fix.changed) corrected = fix.query;

    var list = suggestionsFor(typed.toLowerCase(), corrected);
    if (existing) existing.parentNode.removeChild(existing);
    if (!list.length) return;

    var block = document.createElement('div');
    block.className = 'SearchSuggest';
    block.setAttribute('data-for', typed);
    block.innerHTML = suggestionsMarkup(list);
    results.insertBefore(block, results.firstChild);
  }

  function initDropdown() {
    var input = document.querySelector('.Search__Input');
    var results = document.querySelector('.Search__Results');
    if (!input || !results) return;

    input.addEventListener('focus', loadDict, { once: true });

    var handled = null;
    var observer = new MutationObserver(function () {
      var typed = input.value.trim();
      // `handled` also stops us reacting to our own innerHTML swap below.
      if (!typed || typed === handled) return;

      // Wait for the dictionary rather than bailing: the results can land before
      // it has downloaded, and that mutation is often the only one we get.
      loadDict().then(function (d) {
        if (!d) return null;
        return verifyUnknown(typed).then(function () { return d; });
      }).then(function (d) {
        if (!d || input.value.trim() !== typed) return;

        renderSuggestions(results, typed);
        if (typed === handled) return;

        var pageHasResults = !!results.querySelector('.ProductItem');
        if (pageHasResults) {
          // Same bar as the results page: step in only for a word the catalogue
          // does not contain. Half-typed words are prefixes of real ones, so
          // this stays quiet until the shopper has actually mistyped something.
          if (!repairWords(tokenize(typed)).typo) return;
        } else if (results.textContent.indexOf('No results') === -1) {
          return;                                  // still loading; retry on the next mutation
        }

        handled = typed;
        repair(typed, pageHasResults).then(function (fix) {
          if (!fix) return;
          if (input.value.trim() !== typed) return;        // they kept typing
          fetchSection(fix.query).then(function (html) {
            if (!hasResults(html) || input.value.trim() !== typed) return;
            var target = results.querySelector('.PageLayout__Section') || results;
            target.innerHTML = noticeMarkup(fix.query, typed) + html;
          });
        });
      });
    });
    observer.observe(results, { childList: true, subtree: true });
  }

  function init() {
    if (window.location.pathname.indexOf('/search') !== -1) initResultsPage();
    initDropdown();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Handy from the console:  SilariusSearch.debug.load().then(function(){
  //   console.log(SilariusSearch.debug.words('hikvison camera')); })
  CFG.debug = {
    load: loadDict,
    words: function (q) { return repairWords(tokenize(q)); },
    repair: function (q, hasRes) { return repair(q, !!hasRes); },
    known: function (t) { return isKnownWord(t); },
    verify: function (q) { return verifyUnknown(q).then(function () { return Object.keys(realWords); }); },
    suggest: function (q) {
      var fix = repairWords(tokenize(q));
      return suggestionsFor(q.toLowerCase(), fix.changed ? fix.query : null);
    }
  };
})();
